import type { SupabaseClient } from '@supabase/supabase-js';
import { getSupabaseClient } from './supabase';

export interface InventoryScanSource {
  sourceKind: 'owned_asset' | 'owned_bulk' | 'borrowed_unit' | 'borrowed_stock';
  sourceId: string;
  assetId: string | null;
  itemId: string;
  barcode: string | null;
  serialNumber: string | null;
  itemCode: string | null;
  itemName: string;
  status: string;
  locationId?: string | null;
  subrentalId?: string;
  borrowedUnitId?: string | null;
  vendorName?: string;
  vendorBarcode?: string | null;
  portalBarcode?: string | null;
  trackingMode?: 'quantity' | 'unit';
}
const invalid = () => new Error('The equipment source response was incomplete. Refresh before choosing a source.');
const text = (value: unknown): string => { if(typeof value !== 'string' || !value) throw invalid(); return value; };
const nullable = (value: unknown): string | null => value == null || value === '' ? null : text(value);
const rawCode = (value: unknown): string | null => { if(value === null) return null; if(typeof value !== 'string') throw invalid(); return value; };

export function parseInventoryScanSources(value: unknown): InventoryScanSource[] {
  if(!Array.isArray(value)) throw invalid();
  const sources=value.map((input): InventoryScanSource => {
    if(!input || typeof input !== 'object' || Array.isArray(input)) throw invalid();
    const row=input as Record<string,unknown>;
    if(!['owned_asset','owned_bulk','borrowed_unit','borrowed_stock'].includes(String(row.source_kind))) throw invalid();
    const source: InventoryScanSource={ sourceKind:row.source_kind as InventoryScanSource['sourceKind'],sourceId:text(row.source_id),
      assetId:nullable(row.asset_id),itemId:text(row.item_id),barcode:nullable(row.barcode),serialNumber:nullable(row.serial_number),
      itemCode:nullable(row.item_code),itemName:text(row.item_name),status:text(row.status) };
    if(row.location_id !== undefined) source.locationId=nullable(row.location_id);
    if(source.sourceKind==='owned_asset' && (!source.assetId || source.sourceId!==source.assetId)) throw invalid();
    if(source.sourceKind==='owned_bulk' && (source.assetId || source.sourceId!==source.itemId)) throw invalid();
    if(source.sourceKind.startsWith('borrowed_')) {
      if(source.assetId || !['quantity','unit'].includes(String(row.tracking_mode))) throw invalid();
      source.subrentalId=text(row.subrental_id);source.borrowedUnitId=nullable(row.borrowed_unit_id);
      source.vendorName=text(row.vendor_name);source.vendorBarcode=rawCode(row.vendor_barcode);source.portalBarcode=nullable(row.portal_barcode);
      source.trackingMode=row.tracking_mode as 'quantity'|'unit';
      if(source.sourceKind==='borrowed_unit' && (!source.borrowedUnitId || source.sourceId!==source.borrowedUnitId || source.trackingMode!=='unit' || !source.portalBarcode)) throw invalid();
      if(source.sourceKind==='borrowed_stock' && (source.borrowedUnitId || source.sourceId!==source.subrentalId)) throw invalid();
    }
    return source;
  });
  if(sources.length>50 || new Set(sources.map(row=>`${row.sourceKind}:${row.sourceId}`)).size!==sources.length) throw invalid();
  return sources;
}

export async function resolveWarehouseScanSources(barcode: string, locationId: string | null, eventId: string | null = null,
  options?: {supabase?: SupabaseClient}): Promise<InventoryScanSource[]> {
  const {data,error}=await (options?.supabase??getSupabaseClient()).rpc('resolve_warehouse_scan_sources',{
    p_barcode:barcode,p_location_id:locationId,p_event_id:eventId,
  });
  if(error) throw new Error(error.message.includes('permission_required') ? 'You need inventory access for the selected location.' :
    error.message.includes('too_many_sources') ? 'Too many sources share this code. Select the rental or scan its Portal label.' : 'Equipment sources could not be loaded. Refresh and try again.');
  if(!data || data.eventId!==eventId || data.scannedBarcode!==barcode || (locationId && data.locationId!==locationId)) throw invalid();
  return parseInventoryScanSources(data.sources);
}
export interface QueuedInventoryScan {barcode:string;quantity:number;itemName:string;queueKey?:string;inventorySource?:InventoryScanSource}
export function inventorySourceKey(source:InventoryScanSource) {return `${source.sourceKind}:${source.sourceId}`;}
